import { FastField, FieldProps, getIn } from 'formik';
import React, { forwardRef, ForwardRefRenderFunction, memo, useCallback } from 'react';

import { dateFormat } from '@shared/constants/formats';
import { ISelectDateProps, SelectDate, SelectDateEventType } from '../components/SelectDate';
import { dateWithoutTimeZone, formatDate, getMoment } from '../commonFunctions';
import { TextInputField } from './TextInputField';

export interface ISelectDateFieldProps extends FieldProps, ISelectDateProps {
  id?: string;
}

const SelectDateFieldBase: ForwardRefRenderFunction<HTMLDivElement, ISelectDateFieldProps> = (
  { form, field, disabled, minDate, maxDate, onChange, onBlur, withoutTimeZone, ...props },
  ref
) => {
  const error = getIn(form.errors, field.name);
  const touched = getIn(form.touched, field.name);

  const handleChange = useCallback(
    (event: unknown) => {
      field.onChange(event);
    },
    [form, field]
  );

  return (
    <React.Fragment>
      {disabled ? (
        <FastField
          {...field}
          {...props}
          component={TextInputField}
          disabled={disabled}
          value={formatDate(
            withoutTimeZone ? dateWithoutTimeZone(field.value) : field.value,
            { emptyPlaceholder: '—', getMoment },
            dateFormat
          )}
        />
      ) : (
        <SelectDate
          {...field}
          {...props}
          ref={ref}
          minDate={minDate}
          maxDate={maxDate}
          id={props.id || field.name}
          error={touched && !!error}
          helperText={(touched && error) || ''}
          onBlur={onBlur}
          onChange={(event: SelectDateEventType) => {
            handleChange(event);
            onChange && onChange(event);
          }}
        />
      )}
    </React.Fragment>
  );
};

export const SelectDateField = memo(forwardRef(SelectDateFieldBase));
